import { t } from "i18next";
import ClassNames from "embla-carousel-class-names";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import useMapStore from "@/store/mapStore";
import { AdModel } from "proximiio-js-library/lib/models/ad";
// import useFeaturesStore from "@/store/featuresStore";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Skeleton } from "./ui/skeleton";
import Autoplay from "embla-carousel-autoplay";

function Offers() {
  const ads = useMapStore((state) => state.ads);
  const [offers, setOffers] = useState<AdModel[]>([]);
  const [loading, setLoading] = useState(Array(ads.length).fill(true));
  const [screenHeight, setScreenHeight] = useState<number>();
  /*const features = useFeaturesStore((state) => state.features);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);*/

  /*const adClickHandler = (ad: AdModel) => {
    if (ad.features && ad.features?.length > 0) {
      const feature = features.find((item) => item.id === ad.features![0])!;
      setRouteFinish(feature);
      setOverlayContent(undefined);
      setActiveMenuItem("map");
    }
  };*/

  useEffect(() => {
    const setHeight = () => {
      setScreenHeight(window.innerHeight);
    };

    // Set the height on initial load
    setHeight();

    // Add event listener for window resize to update heights
    window.addEventListener("resize", setHeight);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", setHeight);
    };
  }, []);

  useEffect(() => {
    setOffers(reorderArray(ads));
  }, [ads]);

  const reorderArray = (ads: AdModel[]) => {
    ads.sort((a, b) => a.endDate! - b.endDate!);
    // If there are exactly two elements, sort to put `isDefault: true` first
    if (ads.length === 2 || ads.length > 3) {
      ads.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
    } else if (ads.length === 3) {
      // If there are more than two elements, find the `isDefault: true` element
      const defaultIndex = ads.findIndex((item) => item.isDefault);
      if (defaultIndex !== -1 && defaultIndex !== 1) {
        // Move the `isDefault: true` element to the second position (index 1)
        const [defaultItem] = ads.splice(defaultIndex, 1);
        ads.splice(1, 0, defaultItem);
      } else {
        const [firstItem] = ads.splice(0, 1);
        ads.splice(1, 0, firstItem);
      }
    }

    if (ads.length === 2 || ads.length === 3) {
      ads = ads.concat(ads);
    }

    return ads;
  };

  const handleImageLoad = (index: number) => {
    setLoading((prevLoading) => {
      const newLoadingState = [...prevLoading];
      newLoadingState[index] = false;
      return newLoadingState;
    });
  };

  return (
    <div className="carousel flex h-full w-full flex-col items-center justify-center">
      <h1 className="mb-10 font-bold lg:text-3xl">{t("offers")}</h1>
      <Carousel
        className={cn(
          "mx-auto h-[calc(100%-50px)] w-[90%] lg:mx-0 lg:h-[calc(100%-120px)] lg:max-w-[1900px]",
          screenHeight && screenHeight < 1000 && `lg:w-[80%]`,
          screenHeight && screenHeight < 850 && `lg:w-[60%]`,
          screenHeight && screenHeight < 650 && `lg:w-[40%]`,
        )}
        opts={{
          loop: true,
          startIndex: offers.length === 3 ? 1 : 0,
          direction: "ltr",
        }}
        plugins={[
          ClassNames({}),
          Autoplay({
            delay: 3000,
            stopOnInteraction: true,
          }),
        ]}
        dir="ltr"
      >
        <CarouselContent
          className={cn(
            "ml-0 flex md:-ml-4",
            offers.length === 1 && "justify-center",
          )}
        >
          {offers.map((ad, index) => {
            return (
              <CarouselItem
                key={index}
                className={cn(
                  "w-full basis-auto p-5 md:w-[51%] lg:w-[34%]",
                  offers.length === 2 && "lg:w-[51%]",
                  offers.length === 1 && "w-full",
                )}
                // onClick={() => adClickHandler(ad)}
              >
                {loading[index] && (
                  <div className="flex h-full items-center gap-5 p-8">
                    <Skeleton className="flex h-96 w-full min-w-60 animate-pulse items-center justify-center">
                      <div role="status">
                        <svg
                          aria-hidden="true"
                          className="h-8 w-8 animate-spin fill-primary text-gray-200 dark:text-gray-600"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                        <span className="sr-only">Loading...</span>
                      </div>
                    </Skeleton>
                  </div>
                )}
                <img
                  src={ad.url}
                  alt={ad.name}
                  onLoad={() => handleImageLoad(index)}
                  className={cn(
                    "mx-auto h-full w-auto rounded-xl object-cover shadow-[0px_1px_6px_3px_rgba(0,0,0,0.25)]",
                    loading[index] && "hidden opacity-0",
                  )}
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselPrevious className="left-[calc(50%-2.5rem)] border-primary text-primary hover:border-primary/60 hover:text-primary/60 lg:-left-16 lg:h-12 lg:w-12" />
        <CarouselNext className="right-[calc(50%-2.5rem)] border-primary text-primary hover:border-primary/60 hover:text-primary/60 lg:-right-16 lg:h-12 lg:w-12" />
      </Carousel>
    </div>
  );
}

export default Offers;
